/**
 * A still from a playing video, captured in the browser.
 *
 * Nothing in a deployed environment extracts frames: the ingest worker has
 * never run (issue #110). But when somebody drags out a moment, the browser is
 * already holding the decoded frame they were looking at when they decided it
 * mattered, which is a better still than a server-side pass would pick anyway.
 *
 * Seeking the visible element and putting it back is deliberate. A second,
 * hidden video would re-download the media and would be refused by the signed
 * URL if it had expired in the meantime.
 */

/** How wide a captured still is. A thumbnail, not a frame export. */
const STILL_WIDTH = 240;

export async function captureStill(element: HTMLVideoElement, atSec: number): Promise<Blob | null> {
  // Nothing to draw before metadata: width and height are zero until then.
  if (!element.videoWidth || !element.videoHeight) return null;

  const wasAt = element.currentTime;
  const wasPaused = element.paused;

  try {
    await seekTo(element, atSec);

    const scale = STILL_WIDTH / element.videoWidth;
    const canvas = document.createElement("canvas");
    canvas.width = STILL_WIDTH;
    canvas.height = Math.round(element.videoHeight * scale);

    const context = canvas.getContext("2d");
    if (!context) return null;
    context.drawImage(element, 0, 0, canvas.width, canvas.height);

    return await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", 0.7);
    });
  } catch {
    // A cross-origin frame taints the canvas and toBlob throws. A moment
    // without a still is worth keeping; a failed annotation is not.
    return null;
  } finally {
    // Put the playhead back where the person left it, so capturing a still is
    // not something they have to undo.
    element.currentTime = wasAt;
    if (!wasPaused) void element.play().catch(() => {});
  }
}

/** Resolves once the element has actually moved to a time. */
function seekTo(element: HTMLVideoElement, atSec: number): Promise<void> {
  return new Promise((resolve) => {
    if (Math.abs(element.currentTime - atSec) < 0.05) {
      resolve();
      return;
    }
    const done = () => {
      element.removeEventListener("seeked", done);
      resolve();
    };
    element.addEventListener("seeked", done);
    element.currentTime = atSec;
  });
}
